import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react'
import { FileText, TriangleAlert } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { createHrDocument, createTemplateDocument, createDocument, patchTemplateDocument, updateDocument } from '../../api/document/document'
import UploadedFile from '../display/UploadedFile'
import ModalInput from '../forms/modalForms/modalInput'
import ModalSelect from '../forms/modalForms/modalSelect'
import handleToast from '../HandleToast'
import FileUpload from '../input/FileUpload'
import { phoneNumbers, countrySelectOptions } from '../countries/PhoneDial'
import { useTranslation } from 'react-i18next'
import { modal, button } from '../../themes/styles'

interface ExistingDocument {
  id: string
  title?: string
  version?: string
  status?: string
  countries?: string
  country?: string
  file?: string
}

interface AddDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'hr' | 'template' | 'document'
  onSuccess?: () => void
  assigned_to?: string
  initialFile?: File | null
  editMode?: boolean
  existingDocument?: ExistingDocument
}

const AddDocumentModal: React.FC<AddDocumentModalProps> = ({ isOpen, onClose, type, onSuccess, assigned_to, initialFile, editMode = false, existingDocument }) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()

  const [data, setData] = useState({
    fileName: '',
    file: null as File | null,
    version: '',
    signingStatus: '',
    country: 'all',
  })

  const [errors, setErrors] = useState({
    fileName: false,
    version: false,
    file: false,
    country: false,
  })

  // Load existing document data in edit mode
  useEffect(() => {
    if (editMode && existingDocument && isOpen) {
      const countryName = existingDocument.countries && existingDocument.countries !== 'all' && existingDocument.countries !== 'ALL'
        ? phoneNumbers.find((c) => c.code?.toLowerCase() === existingDocument.countries.toLowerCase())?.name || 'all'
        : 'all'
      
      setData({
        fileName: existingDocument.title || '',
        file: null, // Don't preload the file in edit mode
        version: existingDocument.version || '',
        signingStatus: existingDocument.status || '',
        country: countryName,
      })
    }
  }, [editMode, existingDocument, isOpen])

  useEffect(() => {
    if (initialFile && isOpen && !editMode) {
      setData((prev) => ({ 
        ...prev, 
        file: initialFile,
        fileName: initialFile.name.replace(/\.[^/.]+$/, '')
      }))
    }
  }, [initialFile, isOpen, editMode])

  useEffect(() => {
    if (!isOpen) {
      setData({
        fileName: '',
        file: null,
        version: '',
        signingStatus: '',
        country: 'all',
      })
      setErrors({
        fileName: false,
        version: false,
        file: false,
        country: false,
      })
    }
  }, [isOpen])

  const validateData = () => {
    const errors = {
      fileName: data.fileName === '',
      version: type === 'template' && data.version === '',
      file: !editMode && !data.file, // In edit mode, file is optional
      country: false,
    }

    setErrors(errors)

    return !Object.values(errors).some((error) => error)
  }

  const mutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (editMode) {
        if (type === 'template') {
          return patchTemplateDocument(existingDocument.id, payload)
        } else {
          return updateDocument(existingDocument.id, payload)
        }
      } else {
        if (type === 'document') {
          return createDocument(payload)
        } else if (type === 'hr') {
          return createHrDocument(payload)
        } else {
          return createTemplateDocument(payload)
        }
      }
    },
    onSuccess: () => {
      handleToast(
        toast,
        true,
        editMode ? t('documents.toasts.updatedTitle') : t('documents.toasts.uploadedTitle'),
        editMode ? t('documents.toasts.updatedDescription') : t('documents.toasts.uploadedDescription')
      )
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (onSuccess) {
        onSuccess()
      }
      handleClose()
    },
    onError: (error: Error) => {
      handleToast(
        toast,
        false,
        editMode ? t('documents.toasts.updateFailedTitle') : t('documents.toasts.uploadFailedTitle'),
        error.message
      )
    },
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
    setErrors({ ...errors, [name]: false })
  }

  const handleSubmit = async () => {
    if (!validateData()) return;
    let payload: Record<string, unknown> = {
      title: data.fileName,
      version: data.version,
    };
    
    // Only include file if it's provided
    if (data.file) {
      payload.file = data.file as File;
    }
    
    if (type === 'document' && !editMode) {
      payload.status = data.signingStatus || 'no_signing';
      payload.recipient = assigned_to as string;
    }
    if (type === 'template' && data.country !== 'all') {
      const countryCode = phoneNumbers.find((c) => c.name === data.country)?.code
      payload.country = countryCode || data.country
    }
    mutation.mutate(payload);
  }
  const handleClose = () => {
    setData({
      fileName: '',
      file: null,
      version: '',
      signingStatus: '',
      country: 'all',
    })
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent minW="640px">
        <ModalHeader {...modal.header}>
          {editMode
            ? (type === 'hr' ? t('documents.modals.add.editHrTitle') : type === 'document' ? t('documents.modals.add.editDocumentTitle') : t('documents.modals.add.editTemplateTitle'))
            : (type === 'hr' ? t('documents.modals.add.createHrTitle') : type === 'document' ? t('documents.modals.add.createDocumentTitle') : t('documents.modals.add.createTemplateTitle'))
          }
        </ModalHeader>
        <ModalCloseButton sx={modal.closeButton} />

        <ModalBody>
          <Flex gap="16px">
            <Box width={type === 'hr' ? '100%' : '60%'} mb="16px">
              <ModalInput
                label={t('documents.fields.fileName')}
                type="text"
                name="fileName"
                placeholder={t('documents.placeholders.fileName')}
                value={data.fileName}
                onChange={handleChange}
                isInvalid={!!errors.fileName}
              />
              {errors.fileName && (
                <Flex w="100%" mt={1} alignItems="center" gap="4px">
                  <TriangleAlert size={16} color="var(--chakra-colors-error-100)" />
                  <Text textStyle="b-xs" color="error.100">
                    {t('validation.required', { field: t('documents.fields.fileName') })}
                  </Text>
                </Flex>
              )}
            </Box>
            {type === 'template' && (
              <Box width="40%">
                <ModalInput
                  label={t('common.fields.version')}
                  type="text"
                  name="version"
                  placeholder={t('documents.placeholders.version')}
                  value={data.version}
                  onChange={handleChange}
                  isInvalid={!!errors.version}
                />
                {errors.version && (
                  <Flex w="100%" mt={1} alignItems="center" gap="4px">
                    <TriangleAlert size={16} color="var(--chakra-colors-error-100)" />
                    <Text textStyle="b-xs" color="error.100">
                      {t('validation.required', { field: t('common.fields.version') })}
                    </Text>
                  </Flex>
                )}
              </Box>
            )}
            {type === 'document' && (
              <Box width="40%">
                <ModalSelect
                  label={t('paths.builder.signingType.label')}
                  name="signingStatus"
                  value={data.signingStatus}
                  onChange={(e) => setData((prev) => ({ ...prev, signingStatus: e.target.value }))}
                  options={[
                    { value: '', label: t('paths.builder.signingType.none') },
                    { value: 'pending_signing', label: t('paths.builder.signingType.requireSignature') },
                    { value: 'pending_digital_signature', label: t('paths.builder.signingType.requireDigitalSignature') },
                    { value: 'already_signed', label: t('paths.builder.signingType.alreadySigned') },
                  ]}
                  searchDisabled={true}
                />
              </Box>
            )}
          </Flex>

          {editMode && existingDocument?.file && !data.file && (
            <Box mb="16px">
              <Text textStyle="l-s" mb="8px">{t('documents.labels.currentFile')}</Text>
              <Flex 
                alignItems="center" 
                gap="8px" 
                p="12px" 
                borderRadius="8px" 
                bg="solid.50" 
                borderWidth="1px" 
                borderColor="solid.100"
              >
                <FileText size={20} color="var(--chakra-colors-primary-100)" />
                <Text textStyle="l-xs" flex="1" isTruncated>
                  {existingDocument.file.split('/').pop()?.split('?')[0] || t('documents.labels.documentFileFallback')}
                </Text>
                <Button 
                  size="sm" 
                  variant="link" 
                  onClick={() => window.open(existingDocument.file, '_blank')}
                  color="primary.100"
                >
                  {t('common.actions.view')}
                </Button>
              </Flex>
            </Box>
          )}

          {!data.file ? (
            <FileUpload
              setFile={(file) => setData((prev) => ({ ...prev, file: file as File }))}
              label={editMode ? t('documents.labels.uploadNewFileOptional') : t('documents.labels.selectFile')}
              fileLabel={t('documents.labels.fileLabel')}
              detail={editMode ? t('documents.labels.fileDetailOptional') : t('documents.labels.fileDetail')}
              isInvalid={!!errors.file}
            />
          ) : (
            <UploadedFile
              file={data.file as File}
              onRemove={() => setData((prev) => ({ ...prev, file: null }))}
              label={t('documents.labels.selectFile')}
              isLoading={mutation.isPending}
            />
          )}

          {type === 'template' && (
            <Box width="100%" mt="32px">
              <ModalSelect
                label={t('documents.labels.applicableCountry')}
                placeholder={data.country === 'all' ? t('common.labels.allCountries') : t('common.labels.selectCountry')}
                name="country"
                value={data.country !== 'all' ? data.country : ''}
                options={countrySelectOptions}
                onChange={(e) => {
                  setData((prev) => ({
                    ...prev,
                    country: e.target.value || 'all',
                  }))
                  setErrors({ ...errors, country: false })
                }}
                showCountryFlag
                isInvalid={errors.country}
              />
            </Box>
          )}
        </ModalBody>
        <ModalFooter {...modal.footer}>
          <Button {...button.secondary} onClick={handleClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button {...button.primary} onClick={handleSubmit} isLoading={mutation.isPending}>
            {editMode ? t('common.actions.update') : t('common.actions.submit')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default AddDocumentModal
